// src/components/ConfirmacaoPagamento.jsx

import React from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Stack,
  Paper,
  AlertTitle
} from '@mui/material';
import { Check as CheckIcon } from '@mui/icons-material';
import { formatarMoeda } from '../../utils/formatters';
import pagamentoService from '../../services/pagamentoService';
import pagamentoPublicoService from '../../services/pagamentoPublicoService';

const ConfirmacaoPagamento = ({
  open,
  onClose,
  pagamento,
  loading,
  error,
  success,
  onConfirmar,
  modo = 'dashboard'  // Novo parâmetro
}) => {
  const formatarDetalhesMetodo = (pagamento) => {
    if (!pagamento) return { titulo: '', detalhes: '' };
  
    const dadosPagamento = pagamento.dadosPagamentoJSON || {};
  
    switch (pagamento.metodoPagamento) {
      case 'MPESA':
      case 'EMOLA':
        return {
          titulo: pagamento.metodoPagamento,
          detalhes: dadosPagamento.numeroTelefone 
            ? `Número: +258 ${dadosPagamento.numeroTelefone}`
            : 'Número não fornecido'
        };
  
      case 'BCI':
      case 'MOZABANK':
      case 'STANDARDBANK':
        return {
          titulo: pagamento.metodoPagamento,
          detalhes: `Entidade: ${dadosPagamento.entidade || 'N/A'}\nReferência: ${dadosPagamento.referencia || 'N/A'}`
        };
  
      case 'CARTAO':
        return {
          titulo: 'Cartão de Crédito/Débito',
          detalhes: dadosPagamento.numero 
            ? `Cartão: **** **** **** ${dadosPagamento.numero.slice(-4)}\nValidade: ${dadosPagamento.validade}`
            : 'Dados do cartão não disponíveis'
        };
  
      default:
        return {
          titulo: pagamento.metodoPagamento || 'Método não especificado',
          detalhes: 'Sem detalhes adicionais'
        };
    }
  };

  const renderDetalhesAdicionais = () => {
    if (modo === 'publico') {
      return (
        <Alert severity="info" sx={{ mt: 2 }}>
          <AlertTitle>Próximos Passos</AlertTitle>
          <Typography variant="body2">
            • Você receberá um email com a confirmação do pagamento<br />
            • O status será atualizado automaticamente no seu dashboard<br />
            • Em caso de dúvidas, entre em contato com nosso suporte
          </Typography>
        </Alert>
      );
    }
    return null;
  };

  const renderConteudo = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      );
    }

    // Mostrar mensagem de sucesso quando o pagamento for confirmado
    if (pagamento?.status === 'CONFIRMADO' || success) {
      return (
        <Stack spacing={2} alignItems="center">
          <Box sx={{ 
            width: 60, 
            height: 60, 
            borderRadius: '50%', 
            bgcolor: 'success.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2
          }}>
            <CheckIcon sx={{ color: 'white', fontSize: 40 }} />
          </Box>
          <Typography variant="h6" align="center" color="success.main">
            Pagamento Realizado com Sucesso!
          </Typography>
          <Typography color="text.secondary" align="center">
            Seu pagamento foi processado e confirmado
          </Typography>
          <Alert severity="success" sx={{ mt: 2 }}>
            <AlertTitle>Comprovante Disponível</AlertTitle>
            Um e-mail com o comprovante será enviado para você em instantes.
          </Alert>
          <Box sx={{ mt: 3 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => window.location.href = process.env.FRONTEND_URL}
            >
              Voltar ao Início
            </Button>
          </Box>
        </Stack>
      );
    }

    if (error) {
      return (
        <Alert 
          severity="error" 
          sx={{ mb: 2 }}
          action={modo === 'publico' && (
            <Button 
              color="inherit" 
              size="small"
              href={`${process.env.FRONTEND_URL}/dashboard`}
            >
              Ir para Dashboard
            </Button>
          )}
        >
          {error}
        </Alert>
      );
    }

    if (!pagamento) {
      return (
        <Alert severity="warning">
          Dados do pagamento não disponíveis
        </Alert>
      );
    }

    const { titulo, detalhes } = formatarDetalhesMetodo(pagamento);

    return (
      <Stack spacing={2}>
        <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50' }}>
          <Typography variant="subtitle2" gutterBottom>
            Detalhes da Prestação
          </Typography>
          <Typography variant="body2">
            {pagamento.prestacao?.mes || 'Mês não especificado'}
          </Typography>
          <Typography variant="h6" color="primary">
            {formatarMoeda(pagamento.valorPago || 0)}
          </Typography>
        </Paper>

        <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50' }}>
          <Typography variant="subtitle2" gutterBottom>
            Método de Pagamento
          </Typography>
          <Typography variant="body1">{titulo}</Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
            {detalhes}
          </Typography>
        </Paper>

        {(pagamento.valorMulta > 0) && (
          <Alert severity="warning">
            Este valor inclui multa de {formatarMoeda(pagamento.valorMulta)}
          </Alert>
        )}

        {renderDetalhesAdicionais()}
      </Stack>
    );
  };

  const renderActions = () => {
    if (pagamento?.status === 'CONFIRMADO' || success) {
      return (
        <DialogActions>
          <Button 
            onClick={() => window.location.href = process.env.FRONTEND_URL}
            variant="contained"
            color="primary"
          >
            Voltar ao Início
          </Button>
        </DialogActions>
      );
    }

    if (!loading) {
      return (
        <DialogActions>
          <Button onClick={onClose}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={onConfirmar}
            disabled={loading || !pagamento}
          >
            {loading ? (
              <>
                <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />
                Processando...
              </>
            ) : 'Confirmar Pagamento'}
          </Button>
        </DialogActions>
      );
    }

    return null;
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        {pagamento?.status === 'CONFIRMADO' || success ? 'Confirmação' : 'Confirmar Pagamento'}
      </DialogTitle>
      
      <DialogContent>
        {renderConteudo()}
      </DialogContent>

      {renderActions()}
    </Dialog>
  );
};

// Atualizar PropTypes
ConfirmacaoPagamento.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirmar: PropTypes.func.isRequired,
  pagamento: PropTypes.shape({
    id: PropTypes.number,
    metodoPagamento: PropTypes.oneOf([
      'MPESA',
      'EMOLA', 
      'BCI',
      'STANDARDBANK',
      'MOZABANK',
      'CARTAO'
    ]),
    valorPago: PropTypes.number,
    valorMulta: PropTypes.number,
    status: PropTypes.oneOf([
      'PENDENTE',
      'CONFIRMADO',
      'REJEITADO',
      'CANCELADO',
      'PROCESSANDO'
    ]),
    dataPagamento: PropTypes.string,
    dadosPagamentoJSON: PropTypes.shape({
      numeroTelefone: PropTypes.string,
      entidade: PropTypes.string,
      referencia: PropTypes.string,
      numero: PropTypes.string,
      validade: PropTypes.string,
      cvv: PropTypes.string,
      bandeira: PropTypes.string,
      ultimosDigitos: PropTypes.string
    }),
    prestacao: PropTypes.shape({
      id: PropTypes.number,
      mes: PropTypes.string,
      valor: PropTypes.number,
      dataVencimento: PropTypes.string,
      status: PropTypes.string,
      tipo: PropTypes.string,
      planoId: PropTypes.number,
      vinculoId: PropTypes.number,
      estudanteId: PropTypes.number
    }),
    plano: PropTypes.shape({
      id: PropTypes.number,
      descricao: PropTypes.string,
      instituicaoId: PropTypes.number,
      metodosDisponiveisJSON: PropTypes.object
    }),
    estudante: PropTypes.shape({
      id: PropTypes.number,
      nome: PropTypes.string,
      email: PropTypes.string,
      matricula: PropTypes.string
    }),
    comprovantePagamento: PropTypes.string,
    observacoes: PropTypes.string
  }),
  loading: PropTypes.bool,
  error: PropTypes.string,
  success: PropTypes.bool,
  onDownloadComprovante: PropTypes.func,
  onVerificarStatus: PropTypes.func,
  modo: PropTypes.oneOf(['dashboard', 'publico'])  // Novo PropType
};

ConfirmacaoPagamento.defaultProps = {
  pagamento: null,
  loading: false,
  error: null,
  success: false,
  onDownloadComprovante: () => {},
  onVerificarStatus: () => {},
  modo: 'dashboard'  // Valor padrão
};

export default ConfirmacaoPagamento;
